import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Trash2Icon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isKeyEnter } from '@/util/util';

interface Props {
  itemKey: string;
  itemValue: string;
  index: number;
  keys: string[];
  onAdd: (key: string, value: string, options?: Record<string, string | number>) => void;
  onUpdate: (
    oldKey: string,
    itemKey: string,
    itemValue: string,
    options?: Record<string, string | number>
  ) => void;
  onKeyBlur?: (
    keyRef: string,
    options?: Record<string, string | number> | string,
    itemValue?: string
  ) => void;
  onDelete: (key: string) => void;
  onUpload?: (key: string, oldKey: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: 'small' | 'medium';
  showDelete?: boolean;
  error?: boolean;
  readOnly?: boolean;
}

const inputBaseClass =
  'h-full w-full bg-transparent text-sm leading-5 text-muted-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed';

const KeyValueRow = memo(
  ({
    itemKey,
    itemValue,
    index,
    keys,
    onAdd,
    onUpdate,
    onKeyBlur,
    onDelete,
    onUpload,
    size = 'medium',
    showDelete = true,
    error,
    readOnly,
  }: Props) => {
    const { t } = useTranslation();
    const keyRef = useRef<HTMLInputElement>(null);
    const valueRef = useRef<HTMLInputElement>(null);
    const [isKeyEmpty, setIsKeyEmpty] = useState(itemKey === '');

    useEffect(() => {
      if (keyRef.current) {
        keyRef.current.value = itemKey;
      }
      if (valueRef.current) {
        valueRef.current.value = itemValue;
      }
    }, [itemKey, itemValue]);

    const handleKeyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value.trim();
      keyRef.current!.value = text;
      setIsKeyEmpty(keyRef.current!.value === '');
    }, []);

    const handleValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      valueRef.current!.value = e.target.value;
    }, []);

    useEffect(() => {
      if (keys.length && !itemKey) {
        keyRef.current?.focus();
      }
    }, [itemKey, keys.length]);

    const onValueBlur = useCallback(() => {
      if (itemKey === '' && itemValue === '') {
        if (keyRef.current!.value !== '' && valueRef.current!.value !== '') {
          onAdd(keyRef.current!.value, valueRef.current!.value, { index });
          keyRef.current!.value = '';
          valueRef.current!.value = '';
        }
        return;
      }

      const currentKey = keyRef.current!.value;
      const currentValue = valueRef.current!.value;

      if (currentKey !== itemKey || currentValue !== itemValue) {
        onUpdate(itemKey, currentKey, currentValue, { focus: 'value', index });
      }
    }, [index, itemKey, itemValue, onAdd, onUpdate]);

    const handleKeyBlur = useCallback(() => {
      if (itemKey && itemKey !== keyRef.current!.value && keyRef.current!.value) {
        onKeyBlur?.(keyRef.current!.value, { index, focus: 'key' });
      }

      if (index !== keys.length && !keyRef.current!.value) {
        keyRef.current?.focus();
      }

      if (index === keys.length) {
        onKeyBlur?.(keyRef.current!.value, { index, focus: 'key' });
      }
    }, [itemKey, index, keys.length, onKeyBlur]);

    const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isKeyEnter(e)) {
        (e.target as HTMLInputElement).blur();
        valueRef.current?.focus();
      }
    }, []);

    const onValueUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isKeyEnter(e)) {
        (e.target as HTMLInputElement).blur();
      }
    }, []);

    const handleDelete = useCallback(() => {
      onDelete(itemKey);
    }, [itemKey, onDelete]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpload?.(keyRef.current!.value, itemKey, e);
    };

    return (
      <div
        className={cn(
          'group relative mb-3 flex cursor-pointer items-center rounded-md border border-border bg-background text-sm leading-5 shadow-[0_1px_0_rgba(0,0,0,0.05),0_0_8px_rgba(0,0,0,0.03),0_30px_30px_rgba(0,0,0,0.02)] transition-colors hover:border-border',
          size === 'medium' ? 'h-11' : 'h-10',
          error && 'border-destructive/40 hover:border-destructive/40'
        )}
      >
        <div className="flex h-full flex-1 items-center border-r border-dashed border-border px-6 py-3">
          <input
            ref={keyRef}
            aria-label={t('common.type_key')}
            defaultValue={itemKey}
            placeholder={t('common.type_key')}
            autoFocus={!itemKey}
            onKeyUp={onKeyUp}
            onChange={handleKeyChange}
            onBlur={handleKeyBlur}
            disabled={readOnly}
            className={inputBaseClass}
          />
        </div>
        <div className="flex h-full flex-[2] items-center px-6 py-3">
          <input
            ref={valueRef}
            aria-label={t('common.type_value')}
            defaultValue={itemValue}
            placeholder={t('common.type_value')}
            onKeyUp={onValueUp}
            onBlur={onValueBlur}
            onChange={handleValueChange}
            disabled={readOnly}
            className={inputBaseClass}
          />
        </div>

        {onUpload && !isKeyEmpty && (
          <div className="relative mr-1 ml-3 flex size-8 items-center justify-center rounded-md hover:cursor-pointer hover:bg-selected-soft has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ring">
            <input
              onChange={handleUpload}
              type="file"
              aria-label={t('common.import')}
              className="peer absolute inset-0 z-1 size-full cursor-pointer opacity-0"
            />
            {/* Decorative: the file input above is the real, focusable control. */}
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              tabIndex={-1}
              aria-hidden
              className="p-0 opacity-0 transition group-hover:opacity-100 peer-focus-visible:opacity-100"
            >
              <UploadIcon size={18} />
            </Button>
          </div>
        )}

        {showDelete && !readOnly && (
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            onClick={handleDelete}
            aria-label={t('common.delete')}
            className="mr-3 opacity-0 transition group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100 hover:[&_svg]:text-destructive"
          >
            <Trash2Icon size={18} />
          </Button>
        )}

        {error && (
          <span className="absolute -bottom-5 text-xs leading-4 text-destructive">
            {t('bundles.duplicate_key')}
          </span>
        )}
      </div>
    );
  }
);

KeyValueRow.displayName = 'KeyValueRow';

export default KeyValueRow;
